/* eslint-disable @typescript-eslint/no-explicit-any */
import { AsyncValidatorFn, FormGroup } from '@angular/forms';
import { ArrayInput, BaseInput, ValidatorOrOptions } from './inputs';
import { BaseGroup } from './inputs/base-group';

export interface FormHeader {
    show: boolean;
    title?: string;
    closeButton?: boolean;
    actionClose?: () => void;
}

export interface FormFooter {
    submit?: {
        text?: string;
        disabled?: boolean;
        containerClass?: string[];
    };
    clear?: {
        show?: boolean;
        text?: string;
    };
}

export type EngFormControl<T> = BaseInput<T> | BaseGroup<T> | ArrayInput;

export interface FormContructor<T = any> {
    header: FormHeader;
    footer?: FormFooter;
    controls: {
        [key: string]: EngFormControl<T>;
    };
    /**
     * Indica se è un form di recerca
     */
    filter?: boolean;
    skipDestroy?: boolean;
    validatorOrOpts?: ValidatorOrOptions;
    asyncValidator?: AsyncValidatorFn | AsyncValidatorFn[] | null;
    onDestroy?: () => void;
}

export class Form extends FormGroup {
    header: FormHeader;
    footer?: FormFooter;
    filter = false;
    skipDestroy = false;

    override controls!: {
        [key: string]: BaseInput;
    };

    private _onDestroy: () => void;

    constructor(options: FormContructor) {
        super(options.controls, options.validatorOrOpts, options.asyncValidator);
        this.header = options.header;
        this.footer = options.footer;
        /* eslint-disable @typescript-eslint/ban-ts-comment */
        // @ts-ignore
        this._onDestroy = options.onDestroy;

        if (options.filter !== undefined) {
            this.filter = options.filter;
        }
        if (options.skipDestroy !== undefined) {
            this.skipDestroy = options.skipDestroy;
        }

        /* eslint-disable @typescript-eslint/ban-ts-comment */
        // @ts-ignore
        this._setControlsIndex(this.controls);
    }

    submit(): void {
        throw new Error('Form not used with <ngx-form></ngx-form> element');
    }

    override get(path: (string | number)[] | string): BaseInput | null {
        return super.get(path) as BaseInput;
    }

    override addControl(name: string, control: BaseInput): void {
        super.addControl(name, control);
    }

    override registerControl(name: string, control: BaseInput): BaseInput {
        const retControl = super.registerControl(name, control) as BaseInput;

        if (retControl.index === undefined) {
            retControl.setIndex(this._findMaxIndex(this.controls) + 1);
        } else {
            this._incControlsIndexStartByIndexExceptName(retControl.index, name);
        }

        return retControl;
    }

    /**
     * Retrieves a form array by its path.
     *
     * @param path The path to the control.
     *
     * @returns The form control, or null if not found.
     *
     */
    getArray(path: (string | number)[] | string): ArrayInput | null {
        return super.get(path) as ArrayInput;
    }

    /**
     * Inserisce un nuovo campo prima del campo specificato da beforeName
     * @param name nome del campo da aggiungere
     * @param control campo da inserire
     * @param beforeName nome del campo il cui predecessore sarà field
     */
    addControlBefore(name: string, control: BaseInput, beforeName: string): void {
        const beforeControl = this.get(beforeName);

        if (beforeControl) {
            /* eslint-disable @typescript-eslint/ban-ts-comment */
            // @ts-ignore
            control.setIndex(beforeControl.index);
            /* eslint-disable @typescript-eslint/ban-ts-comment */
            // @ts-ignore
            beforeControl.setIndex(beforeControl.index);
            this.addControl(name, control);
        }
    }

    /**
     * Inserisce un nuovo campo dopo del campo specificato da afterName
     * @param name nome del campo da aggiungere
     * @param control campo da inserire
     * @param afterName nome del campo il cui successore sarà field
     */
    addControlAfter(name: string, control: BaseInput<any>, afterName: string): void {
        const afterControl = this.get(afterName);

        if (afterControl) {
            /* eslint-disable @typescript-eslint/ban-ts-comment */
            // @ts-ignore
            control.setIndex(afterControl.index + 1);
            this.addControl(name, control);
        }
    }

    onDestroy(): void {
        if (this._onDestroy && typeof this._onDestroy === 'function') {
            this._onDestroy();
        }
        for (const name in this.controls) {
            if (this.controls[name] instanceof BaseInput) {
                this.controls[name].unsubscribeSubscriptions();
            }
        }
    }

    /** @internal */
    private _incControlsIndexStartByIndexExceptName(index: number, name: string): void {
        for (const _name in this.controls) {
            if (
                this.controls[_name] &&
                this.controls[_name].index !== undefined &&
                /* eslint-disable @typescript-eslint/ban-ts-comment */
                // @ts-ignore
                this.controls[_name].index >= index &&
                name !== _name
            ) {
                /* eslint-disable @typescript-eslint/ban-ts-comment */
                // @ts-ignore
                this.controls[_name].setIndex(this.controls[_name].index + 1);
            }
        }
    }

    private _setControlsIndex(controls: { [key: string]: BaseInput }): void {
        let i = this._findMaxIndex(controls) + 1;
        for (const name in controls) {
            if (controls[name]) {
                if (controls[name].index === undefined) {
                    controls[name].setIndex(i);
                    i++;
                }
            }
        }
    }

    private _findMaxIndex(controls: { [key: string]: BaseInput }): number {
        let i = 0;
        for (const name in controls) {
            if (
                controls[name] &&
                Number.isInteger(controls[name].index) &&
                /* eslint-disable @typescript-eslint/ban-ts-comment */
                // @ts-ignore
                controls[name].index > i
            ) {
                /* eslint-disable @typescript-eslint/ban-ts-comment */
                // @ts-ignore
                i = controls[name].index;
            }
        }
        return i;
    }
}
